import { ListDisplayElement } from '@/components/custom-elements/list-display'

enum SizeGuideConversionKeys {
  CM = 'CM',
  IN = 'IN'
}

/**
 * Custom Element for handling size conversions between centimeters and inches
 * @extends ListDisplayElement
 */
export class SizeGuideElement extends ListDisplayElement {
  // Static properties for element selectors
  private static readonly SWITCH_KEY = 'input[data-role="switch"]' as const
  private static readonly CONVERT_KEY = '[data-role="convert"]' as const

  private readonly INCH: number

  constructor() {
    super()
    this.INCH = 2.54
  }

  /**
   * Converts a value between CM and IN based on the provided key
   * @param key - The conversion direction (CM or IN)
   * @param value - The string value to convert
   * @returns The converted number or undefined if conversion fails
   */
  private conversionReducer(key: SizeGuideConversionKeys, value: string): number | undefined {
    const number = parseFloat(value)
    if (isNaN(number)) return undefined

    switch (key) {
      case SizeGuideConversionKeys.CM:
        return number * this.INCH
      case SizeGuideConversionKeys.IN:
        return number / this.INCH
      default:
        return undefined
    }
  }

  /**
   * Updates the data-value attribute of an element with the converted value
   * @param element - The element to update
   * @param key - The conversion direction
   * @returns The new converted value or undefined if conversion fails
   */
  private setTrueValue(element: Element, key: SizeGuideConversionKeys): number | undefined {
    const currentValue = element.getAttribute('data-value')
    if (!currentValue) return undefined

    const newValue = this.conversionReducer(key, currentValue)
    if (typeof newValue === 'undefined') return undefined

    element.setAttribute('data-value', newValue.toString())
    return newValue
  }

  /**
   * Handles the conversion when the switch input changes
   * @param e - The change event
   */
  private handleConversion = (e: Event): void => {
    const switchInput = e.currentTarget as HTMLInputElement
    const key = switchInput.checked ? SizeGuideConversionKeys.IN : SizeGuideConversionKeys.CM

    this.querySelectorAll<Element>(SizeGuideElement.CONVERT_KEY).forEach((el) => {
      const newValue = this.setTrueValue(el, key)
      if (typeof newValue !== 'undefined') {
        el.innerHTML = Math.round(newValue).toString()
      }
    })
  }

  /**
   * Adds event listeners to the element
   */
  addEventListeners(): void {
    super.fetchChildren()
    super.addEventListeners()

    const switchElement = this.querySelector<HTMLInputElement>(SizeGuideElement.SWITCH_KEY)
    if (switchElement) {
      switchElement.addEventListener('change', this.handleConversion)
    }
  }

  /**
   * Removes event listeners from the element
   */
  removeEventListeners(): void {
    const switchElement = this.querySelector<HTMLInputElement>(SizeGuideElement.SWITCH_KEY)
    if (switchElement) {
      switchElement.removeEventListener('change', this.handleConversion)
    }
  }

  /**
   * Called when the element is connected to the DOM
   */
  connectedCallback(): void {
    this.addEventListeners()
  }

  /**
   * Called when the element is disconnected from the DOM
   */
  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}
